import { useEffect, useState, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  Stack,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Alert,
  CircularProgress,
  Switch,
  Tooltip,
  FormControlLabel,
  Checkbox,
  FormControl,
  FormGroup,
  FormHelperText,
  RadioGroup,
  Radio,
  Autocomplete,
} from '@mui/material';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshButton from '../components/RefreshButton';
import SendIcon from '@mui/icons-material/Send';
import {
  alertService,
  type AlertConfig,
  type CreateAlertConfigRequest,
  type UpdateAlertConfigRequest,
  type WatcherScope,
} from '../services/alert.service';
import { watcherRegistryService, type WatcherClient } from '../services/watcher-registry.service';
import { ALERT_EVENTS, type AlertEvent } from '@/types/enums.generated';
import { emailDomainProblem, emailDomainsText, emailProblem } from '@/utils/accountRules';
import { useEmailDomainPolicy } from '@/hooks/useAuthConfig';
import { useFieldErrors, type FieldErrors } from '@/utils/formErrors';

// QA #68: which input a refusal belongs to (VALIDATION_ERROR fields already
// carry the request's JSON keys: alert_email / events / dedup_window_minutes).
const ALERT_FIELDS: Record<string, string> = { ALERT_EVENT_INVALID: 'events' };

// The vocabulary comes from the backend (internal/alerts/events.go via the
// ADR-043 codegen); every value has a trigger site since ADR-044 and the
// backend rejects anything else with 400 ALERT_EVENT_INVALID. A new event
// fails to compile here until it gets a label.
const EVENT_LABEL_MAP: Record<AlertEvent, string> = {
  backup_failed: '備份失敗',
  schedule_failed: '排程失敗',
  backup_cancelled: '備份取消',
  watcher_offline: 'Watcher 離線',
  destination_unreachable: '備份目的地連線失敗',
};

// When each event is raised (QA #100), shown under every checkbox and as the
// chip tooltip. Like EVENT_LABEL_MAP, a new event fails to compile until it
// gets a rule. Keep in step with the trigger sites listed in
// backend/internal/alerts/events.go.
const EVENT_RULE_MAP: Record<AlertEvent, string> = {
  backup_failed: '主控台上傳的檔案備份失敗（含上傳中斷、交給 worker 失敗、雲端備份未完成）。',
  schedule_failed: 'File Watcher 回報排程備份失敗。',
  backup_cancelled: '主控台取消備份工作、上傳中按取消，或 File Watcher 回報排程備份已取消。',
  watcher_offline:
    '已啟用且曾連線的 File Watcher 超過 5 分鐘沒有回報（每分鐘檢查），每次斷線寄一次；可選全部或指定的 Watcher。停止服務時若仍有備份在執行，會等收尾結束後才算離線。',
  destination_unreachable:
    '系統每 30 分鐘自動檢查地端主機與雲端目的地的連線，失敗自動重試 3 次仍失敗才寄出，每次斷線寄一次。',
};

// Who receives mail and how dedup works (QA #100, decision 2026-11-01;
// backend internal/alerts/visibility.go).
const RECIPIENT_RULE =
  '告警只寄到每筆設定指定的信箱。每筆設定會收到你有權限看到的資源所發生的事件（最高管理員：全部；子管理員：所屬群組；一般使用者：自己的）；同一設定對同一資源的同一事件，在去重時間內只寄一次。';

// The page is admin / sub_admin only (QA #60, config/navigation.ts and the
// backend /alerts gate), and both roles can receive every event. The QA #54
// per-role filtering (operators could not receive watcher_offline /
// schedule_failed) went away with operator access.
const EVENT_OPTIONS: { value: AlertEvent; label: string; rule: string }[] = ALERT_EVENTS.map((value) => ({
  value,
  label: EVENT_LABEL_MAP[value],
  rule: EVENT_RULE_MAP[value],
}));

const EVENT_LABELS: Record<string, string> = EVENT_LABEL_MAP;
const EVENT_RULES: Record<string, string> = EVENT_RULE_MAP;

const DEDUP_HINT = '同一資源的同一事件，在此時間內只通知一次（不同資源各自計算；0 表示每次都通知）';

// Checkbox label: the event name with its rule as secondary text.
function EventOptionLabel({ label, rule }: { label: string; rule: string }) {
  return (
    <Box sx={{ py: 0.5 }}>
      <Typography variant="body2">{label}</Typography>
      <Typography variant="caption" color="text.secondary" component="div">
        {rule}
      </Typography>
    </Box>
  );
}

// List chip text: watcher_offline says which watchers (QA #100).
function eventChipLabel(event: string, config: AlertConfig): string {
  const label = EVENT_LABELS[event] || event;
  if (event !== 'watcher_offline') return label;
  return config.watcher_scope === 'selected' ? `${label}（${config.watchers.length} 台）` : `${label}（全部）`;
}

function eventChipTooltip(event: string, config: AlertConfig): string {
  const rule = EVENT_RULES[event] ?? '';
  if (event !== 'watcher_offline' || config.watcher_scope !== 'selected') return rule;
  return `${rule}\n指定：${config.watchers.map((w) => w.name).join('、')}`;
}

function watcherOptionLabel(w: WatcherClient): string {
  return w.name;
}

function watcherOptionDetail(w: WatcherClient): string {
  return `${w.group_name || '未分組'} · ${w.owner_username || '—'}`;
}

// The watcher_offline scope chooser (QA #100), shown when 「Watcher 離線」
// is ticked: every visible watcher (including later ones) or a chosen list.
function WatcherScopeField({
  scope,
  ids,
  watchers,
  error,
  onChange,
}: {
  scope: WatcherScope;
  ids: string[];
  watchers: WatcherClient[];
  error?: string;
  onChange: (scope: WatcherScope, ids: string[]) => void;
}) {
  const selected = watchers.filter((w) => ids.includes(w.id));
  return (
    <Box sx={{ pl: 4, pb: 1 }}>
      <RadioGroup
        row
        value={scope}
        onChange={(e) => onChange(e.target.value as WatcherScope, ids)}
      >
        <FormControlLabel value="all" control={<Radio size="small" />} label="全部 Watcher（含之後新增的）" />
        <FormControlLabel value="selected" control={<Radio size="small" />} label="指定 Watcher" />
      </RadioGroup>
      {scope === 'selected' && (
        <Autocomplete
          multiple
          size="small"
          options={watchers}
          value={selected}
          getOptionLabel={watcherOptionLabel}
          isOptionEqualToValue={(a, b) => a.id === b.id}
          onChange={(_, value) => onChange('selected', value.map((w) => w.id))}
          noOptionsText="沒有可選的 File Watcher"
          renderOption={(props, option) => (
            <li {...props} key={option.id}>
              <Box>
                <Typography variant="body2">{option.name}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {watcherOptionDetail(option)}
                </Typography>
              </Box>
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              label="要通知的 File Watcher"
              error={Boolean(error)}
              helperText={error || '只列出你有權限看到的 Watcher（顯示所屬群組與管理人）'}
            />
          )}
        />
      )}
    </Box>
  );
}

// QA #37: the address is checked as it is typed (emailProblem with
// required=false) so a typo shows up under the field before the request.

// The form's own checks, every problem on its own field at once (QA #68).
// domains: QA #94 allowed alert domains, checked only when the address is
// new or changed (pass [] to skip) — the backend does the same.
function alertFormProblems(
  email: string | undefined,
  events: string[] | undefined,
  dedup: number | undefined,
  domains: string[],
  watcherScope?: WatcherScope,
  watcherIds?: string[],
): FieldErrors {
  const problems: FieldErrors = {};
  const emailIssue = emailProblem(email ?? '') || emailDomainProblem(email ?? '', domains);
  if (emailIssue) problems.alert_email = emailIssue.replace('電子信箱', '通知信箱');
  if (!events || events.length === 0) problems.events = '請至少勾選一個要通知的事件類型';
  if (events?.includes('watcher_offline') && watcherScope === 'selected' && (watcherIds ?? []).length === 0) {
    problems.watcher_ids = '選擇「指定 Watcher」時，請至少選一台 File Watcher';
  }
  if (dedup !== undefined && (!Number.isInteger(dedup) || dedup < 0)) {
    problems.dedup_window_minutes = '請輸入 0 以上的整數分鐘數（0 表示每次事件都通知）';
  }
  return problems;
}

export default function AlertSettings() {
  const eventOptions = EVENT_OPTIONS;

  // --- Alert configs state ---
  // QA #94: alert mail may be limited to some domains by the deployment.
  const { alertDomains } = useEmailDomainPolicy();
  const domainHint = alertDomains.length > 0 ? `僅允許使用 ${emailDomainsText(alertDomains)} 的信箱` : undefined;
  const [configs, setConfigs] = useState<AlertConfig[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  // Create dialog
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createForm, setCreateForm] = useState<CreateAlertConfigRequest>({
    alert_email: '',
    events: [],
    dedup_window_minutes: 60,
    watcher_scope: 'all',
    watcher_ids: [],
  });
  // createError / editError are for problems that belong to no field; field
  // problems are marked on the input (QA #68).
  const [createError, setCreateError] = useState('');
  const createFields = useFieldErrors();

  // Edit dialog
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editingConfig, setEditingConfig] = useState<AlertConfig | null>(null);
  const [editForm, setEditForm] = useState<UpdateAlertConfigRequest>({
    alert_email: '',
    events: [],
    is_enabled: true,
    dedup_window_minutes: 60,
    watcher_scope: 'all',
    watcher_ids: [],
  });
  const [editError, setEditError] = useState('');
  const editFields = useFieldErrors();

  // Delete dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deletingConfig, setDeletingConfig] = useState<AlertConfig | null>(null);

  // Test email
  const [testingEmail, setTestingEmail] = useState<string | null>(null);

  // Watchers the user can see, for the watcher_offline picker (QA #100).
  const [watchers, setWatchers] = useState<WatcherClient[]>([]);
  useEffect(() => {
    watcherRegistryService
      .listWatchers()
      .then(setWatchers)
      .catch((err) => console.error('Failed to load watchers:', err));
  }, []);

  // --- Load data ---
  const loadConfigs = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const data = await alertService.listConfigs();
      setConfigs(data);
    } catch (err) {
      console.error('Failed to load alert configs:', err);
      setError('載入告警設定失敗');
    } finally {
      setLoading(false);
    }
  }, []);

  // Only recipients are set here. The sending side is the deployment's
  // SMTP_* (Azure Communication Services configured by infra, ADR-036).
  useEffect(() => {
    loadConfigs();
  }, [loadConfigs]);

  // --- DataGrid columns ---
  const columns: GridColDef[] = useMemo(() => [
    {
      field: 'alert_email',
      headerName: '通知信箱',
      flex: 1,
      minWidth: 200,
      renderCell: (params) => (
        <Typography variant="body2" fontWeight={600}>{params.value}</Typography>
      ),
    },
    {
      field: 'events',
      headerName: '事件類型',
      flex: 1.5,
      minWidth: 250,
      sortable: false,
      renderCell: (params) => {
        const events = params.value as string[];
        return (
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ py: 0.5 }}>
            {events && events.length > 0 ? (
              events.map((event) => (
                <Tooltip
                  key={event}
                  title={<span style={{ whiteSpace: 'pre-line' }}>{eventChipTooltip(event, params.row as AlertConfig)}</span>}
                  arrow
                >
                  <Chip label={eventChipLabel(event, params.row as AlertConfig)} size="small" variant="outlined" />
                </Tooltip>
              ))
            ) : (
              <Typography variant="body2" color="text.secondary">-</Typography>
            )}
          </Stack>
        );
      },
    },
    {
      field: 'is_enabled',
      headerName: '狀態',
      width: 100,
      renderCell: (params) => (
        <Chip
          label={params.value ? '啟用' : '停用'}
          color={params.value ? 'success' : 'default'}
          size="small"
        />
      ),
    },
    {
      field: 'dedup_window_minutes',
      headerName: '去重視窗',
      width: 120,
      renderCell: (params) => (
        <Typography variant="body2">{params.value} 分鐘</Typography>
      ),
    },
    {
      field: 'actions',
      headerName: '操作',
      width: 180,
      sortable: false,
      filterable: false,
      align: 'right',
      headerAlign: 'right',
      renderCell: (params) => {
        const config = params.row as AlertConfig;
        return (
          <Stack direction="row" spacing={0.5} justifyContent="flex-end" alignItems="center">
            <Tooltip title="測試發送">
              <span>
                <IconButton
                  size="small"
                  color="info"
                  onClick={() => handleTestEmail(config.alert_email)}
                  disabled={testingEmail === config.alert_email}
                >
                  {testingEmail === config.alert_email ? (
                    <CircularProgress size={18} />
                  ) : (
                    <SendIcon fontSize="small" />
                  )}
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="編輯">
              <IconButton size="small" color="primary" onClick={() => openEditDialog(config)}>
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="刪除">
              <IconButton size="small" color="error" onClick={() => openDeleteDialog(config)}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        );
      },
    },
  ], [testingEmail]);

  // --- Test email ---
  const handleTestEmail = async (email: string) => {
    try {
      setTestingEmail(email);
      await alertService.sendTestEmail(email);
      setSuccessMsg(`測試信件已發送至 ${email}`);
    } catch (err) {
      console.error('Failed to send test email:', err);
      setError(err instanceof Error ? err.message : '發送測試信件失敗');
    } finally {
      setTestingEmail(null);
    }
  };

  // --- Create config ---
  const openCreateDialog = () => {
    setCreateForm({ alert_email: '', events: [], dedup_window_minutes: 60, watcher_scope: 'all', watcher_ids: [] });
    setCreateError('');
    createFields.clear();
    setCreateDialogOpen(true);
  };

  const handleCreateConfig = async () => {
    // Backend CreateAlertConfigRequest: alert_email required,email; events required,min=1
    const problems = alertFormProblems(
      createForm.alert_email,
      createForm.events,
      createForm.dedup_window_minutes,
      alertDomains,
      createForm.watcher_scope,
      createForm.watcher_ids,
    );
    if (Object.keys(problems).length > 0) {
      createFields.setErrors(problems);
      return;
    }

    try {
      setCreating(true);
      setCreateError('');
      createFields.clear();
      await alertService.createConfig(createForm);
      setCreateDialogOpen(false);
      setSuccessMsg('告警設定已建立');
      loadConfigs();
    } catch (err) {
      console.error('Failed to create alert config:', err);
      setCreateError(createFields.apply(err, ALERT_FIELDS, '建立告警設定失敗，請稍後再試'));
    } finally {
      setCreating(false);
    }
  };

  const handleCreateEventToggle = (event: string) => {
    const events = createForm.events;
    if (events.includes(event)) {
      setCreateForm({ ...createForm, events: events.filter((e) => e !== event) });
    } else {
      setCreateForm({ ...createForm, events: [...events, event] });
    }
    createFields.clearField('events');
  };

  // --- Edit config ---
  const openEditDialog = (config: AlertConfig) => {
    setEditingConfig(config);
    setEditForm({
      alert_email: config.alert_email,
      events: [...config.events],
      is_enabled: config.is_enabled,
      dedup_window_minutes: config.dedup_window_minutes,
      watcher_scope: config.watcher_scope ?? 'all',
      watcher_ids: [...(config.watcher_ids ?? [])],
    });
    setEditError('');
    setEditDialogOpen(true);
  };

  // Field hints are dropped on close (not on open) so openEditDialog, which
  // the memoised columns call, keeps capturing nothing but state setters.
  const closeEditDialog = () => {
    setEditDialogOpen(false);
    editFields.clear();
  };

  const handleEditConfig = async () => {
    if (!editingConfig) return;
    const emailChanged =
      (editForm.alert_email ?? '').trim().toLowerCase() !== (editingConfig?.alert_email ?? '').trim().toLowerCase();
    const problems = alertFormProblems(
      editForm.alert_email,
      editForm.events,
      editForm.dedup_window_minutes,
      emailChanged ? alertDomains : [],
      editForm.watcher_scope,
      editForm.watcher_ids,
    );
    if (Object.keys(problems).length > 0) {
      editFields.setErrors(problems);
      return;
    }

    try {
      setEditing(true);
      setEditError('');
      editFields.clear();
      await alertService.updateConfig(editingConfig.id, editForm);
      closeEditDialog();
      setSuccessMsg('告警設定已更新');
      loadConfigs();
    } catch (err) {
      console.error('Failed to update alert config:', err);
      setEditError(editFields.apply(err, ALERT_FIELDS, '更新告警設定失敗，請稍後再試'));
    } finally {
      setEditing(false);
    }
  };

  const handleEditEventToggle = (event: string) => {
    const events = editForm.events || [];
    if (events.includes(event)) {
      setEditForm({ ...editForm, events: events.filter((e) => e !== event) });
    } else {
      setEditForm({ ...editForm, events: [...events, event] });
    }
    editFields.clearField('events');
  };

  // --- Delete config ---
  const openDeleteDialog = (config: AlertConfig) => {
    setDeletingConfig(config);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfig = async () => {
    if (!deletingConfig) return;

    try {
      setDeleting(true);
      await alertService.deleteConfig(deletingConfig.id);
      setDeleteDialogOpen(false);
      setSuccessMsg(`告警設定「${deletingConfig.alert_email}」已刪除`);
      loadConfigs();
    } catch (err) {
      console.error('Failed to delete alert config:', err);
      setError('刪除告警設定失敗');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
        <Box>
          <Typography variant="h4" gutterBottom>
            告警設定
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            管理告警通知信箱；通知信由平台內建的寄信服務寄出
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {RECIPIENT_RULE}
          </Typography>
        </Box>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={openCreateDialog}
          size="large"
        >
          新增告警
        </Button>
      </Box>

      {/* Success / Error alerts */}
      {successMsg && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMsg('')}>
          {successMsg}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Section 1: My Alert Configs */}
      <Paper sx={{ p: 3, mt: 3 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6">我的告警設定</Typography>
          <RefreshButton onClick={loadConfigs} loading={loading} />
        </Stack>

        <DataGrid
          rows={configs}
          columns={columns}
          loading={loading}
          disableRowSelectionOnClick
          autoHeight
          pageSizeOptions={[10, 20, 50]}
          initialState={{
            pagination: { paginationModel: { pageSize: 20, page: 0 } },
          }}
          localeText={{
            noRowsLabel: '暫無告警設定',
          }}
          slotProps={{
            pagination: {
              labelRowsPerPage: '每頁筆數：',
              labelDisplayedRows: ({ from, to, count }: { from: number; to: number; count: number }) =>
                `${from}-${to} / 共 ${count}`,
            },
          }}
          sx={{
            border: 'none',
          }}
        />
      </Paper>

      {/* Create Alert Config Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={() => !creating && setCreateDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Stack direction="row" spacing={1} alignItems="center">
            <AddIcon color="primary" />
            <Typography variant="h6">新增告警設定</Typography>
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2.5} sx={{ mt: 1 }}>
            {createError && (
              <Alert severity="error">{createError}</Alert>
            )}

            <TextField
              fullWidth
              label="通知信箱"
              type="email"
              value={createForm.alert_email}
              onChange={(e) => setCreateForm({ ...createForm, alert_email: e.target.value })}
              required
              placeholder="user@example.com"
              {...createFields.bind('alert_email', emailProblem(createForm.alert_email, false) || domainHint)}
              error={Boolean(createFields.errors.alert_email) || Boolean(emailProblem(createForm.alert_email, false))}
            />

            <FormControl component="fieldset" error={Boolean(createFields.errors.events)}>
              <Typography variant="subtitle2" gutterBottom>
                事件類型
              </Typography>
              <FormGroup>
                {eventOptions.map((option) => (
                  <FormControlLabel
                    key={option.value}
                    sx={{ alignItems: 'flex-start', mb: 0.5 }}
                    control={
                      <Checkbox
                        name="events"
                        sx={{ pt: 0.5 }}
                        checked={createForm.events.includes(option.value)}
                        onChange={() => handleCreateEventToggle(option.value)}
                      />
                    }
                    label={<EventOptionLabel label={option.label} rule={option.rule} />}
                  />
                ))}
              </FormGroup>
              {createForm.events.includes('watcher_offline') && (
                <WatcherScopeField
                  scope={createForm.watcher_scope ?? 'all'}
                  ids={createForm.watcher_ids ?? []}
                  watchers={watchers}
                  error={createFields.errors.watcher_ids}
                  onChange={(scope, ids) => {
                    setCreateForm({ ...createForm, watcher_scope: scope, watcher_ids: ids });
                    createFields.clearField('watcher_ids');
                  }}
                />
              )}
              {createFields.errors.events && <FormHelperText>{createFields.errors.events}</FormHelperText>}
            </FormControl>

            <TextField
              fullWidth
              label="去重視窗（分鐘）"
              type="number"
              value={createForm.dedup_window_minutes}
              onChange={(e) =>
                setCreateForm({ ...createForm, dedup_window_minutes: parseInt(e.target.value, 10) || 0 })
              }
              InputProps={{ inputProps: { min: 0 } }}
              {...createFields.bind('dedup_window_minutes', DEDUP_HINT)}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setCreateDialogOpen(false)} disabled={creating}>
            取消
          </Button>
          <Button
            variant="contained"
            onClick={handleCreateConfig}
            disabled={creating}
            startIcon={creating ? <CircularProgress size={16} /> : <AddIcon />}
          >
            {creating ? '建立中...' : '建立告警'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Alert Config Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => !editing && closeEditDialog()}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Stack direction="row" spacing={1} alignItems="center">
            <EditIcon color="primary" />
            <Typography variant="h6">編輯告警設定</Typography>
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2.5} sx={{ mt: 1 }}>
            {editError && (
              <Alert severity="error">{editError}</Alert>
            )}

            <TextField
              fullWidth
              label="通知信箱"
              type="email"
              value={editForm.alert_email || ''}
              onChange={(e) => setEditForm({ ...editForm, alert_email: e.target.value })}
              required
              {...editFields.bind('alert_email', emailProblem(editForm.alert_email ?? '', false) || domainHint)}
              error={Boolean(editFields.errors.alert_email) || Boolean(emailProblem(editForm.alert_email ?? '', false))}
            />

            <FormControl component="fieldset" error={Boolean(editFields.errors.events)}>
              <Typography variant="subtitle2" gutterBottom>
                事件類型
              </Typography>
              <FormGroup>
                {eventOptions.map((option) => (
                  <FormControlLabel
                    key={option.value}
                    sx={{ alignItems: 'flex-start', mb: 0.5 }}
                    control={
                      <Checkbox
                        name="events"
                        sx={{ pt: 0.5 }}
                        checked={(editForm.events || []).includes(option.value)}
                        onChange={() => handleEditEventToggle(option.value)}
                      />
                    }
                    label={<EventOptionLabel label={option.label} rule={option.rule} />}
                  />
                ))}
              </FormGroup>
              {(editForm.events || []).includes('watcher_offline') && (
                <WatcherScopeField
                  scope={editForm.watcher_scope ?? 'all'}
                  ids={editForm.watcher_ids ?? []}
                  watchers={watchers}
                  error={editFields.errors.watcher_ids}
                  onChange={(scope, ids) => {
                    setEditForm({ ...editForm, watcher_scope: scope, watcher_ids: ids });
                    editFields.clearField('watcher_ids');
                  }}
                />
              )}
              {editFields.errors.events && <FormHelperText>{editFields.errors.events}</FormHelperText>}
            </FormControl>

            <TextField
              fullWidth
              label="去重視窗（分鐘）"
              type="number"
              value={editForm.dedup_window_minutes}
              onChange={(e) =>
                setEditForm({ ...editForm, dedup_window_minutes: parseInt(e.target.value, 10) || 0 })
              }
              InputProps={{ inputProps: { min: 0 } }}
              {...editFields.bind('dedup_window_minutes', DEDUP_HINT)}
            />

            <FormControlLabel
              control={
                <Switch
                  checked={editForm.is_enabled ?? true}
                  onChange={(e) => setEditForm({ ...editForm, is_enabled: e.target.checked })}
                />
              }
              label={editForm.is_enabled ? '啟用' : '停用'}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={closeEditDialog} disabled={editing}>
            取消
          </Button>
          <Button
            variant="contained"
            onClick={handleEditConfig}
            disabled={editing}
            startIcon={editing ? <CircularProgress size={16} /> : <EditIcon />}
          >
            {editing ? '更新中...' : '儲存變更'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => !deleting && setDeleteDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          <Stack direction="row" spacing={1} alignItems="center">
            <DeleteIcon color="error" />
            <Typography variant="h6">確認刪除</Typography>
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Alert severity="warning">
              確定要刪除告警設定「{deletingConfig?.alert_email}」嗎？此操作無法復原。
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>
            取消
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteConfig}
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} /> : <DeleteIcon />}
          >
            {deleting ? '刪除中...' : '確認刪除'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
